import { isNotificationEventEnabled } from "../domain/settings/system-settings.js";
import type { AuditRecorder } from "../audit/audit-recorder.js";
import type { SettingsSecretStore, SystemSettingsRepository } from "../repositories/system-settings-repository.js";
import { SystemSettingsProvider } from "../application/services/system-settings-provider.js";
import { DocumentNumberingService, type DocumentNumberAllocator } from "../application/services/document-numbering-service.js";
import { SystemSettingsService, type SettingsDiagnosticsProbe, type DatabaseResetResult } from "../application/services/system-settings-service.js";
import { notificationService } from "../application/services/notification-service.js";
import type { SecuritySettings } from "../domain/settings/system-settings.js";
import type { ImportMappingOptions } from "../domain/import/equipment-import.js";
import type { RequestContext } from "../application/context/request-context.js";

export interface SystemSettingsWiringTargets {
  /** RI, ME, LP, PM, USC, B-Scan, AVR e Minuta. */
  readonly numberedServices: ReadonlyArray<{ attachNumberAllocator(allocator: DocumentNumberAllocator): void }>;
  /** PM, USC e B-Scan (base de END). */
  readonly endServices: ReadonlyArray<{ attachEndRequirements(provider: (documentType: string) => Promise<{ requirePhoto: boolean; requireCroqui: boolean } | null>): void }>;
  readonly adminService: { attachPasswordPolicy(provider: () => Promise<SecuritySettings>): void };
  readonly importService: { attachMappingOptions(provider: () => Promise<ImportMappingOptions>): void };
  readonly lookups: {
    clientCode(clientId: string): Promise<string | null>;
    projectCode(projectId: string): Promise<string | null>;
    unitCode(unitAreaId: string): Promise<string | null>;
    userName(userId: string): Promise<string | null>;
  };
  readonly databaseReset?: {
    handler(context: RequestContext, reason?: string): Promise<DatabaseResetResult>;
    adminAuth(identifier: string, password: string): Promise<{ user?: any }>;
  };
}

/**
 * Liga CONFIGURAÇÕES aos módulos que as consomem. É a MESMA função nas duas
 * composições (navegador e servidor): uma ligação escrita duas vezes
 * divergiria na primeira alteração.
 */
export function wireSystemSettings(
  repo: SystemSettingsRepository,
  secrets: SettingsSecretStore,
  probe: SettingsDiagnosticsProbe,
  audit: AuditRecorder | undefined,
  targets: SystemSettingsWiringTargets
): { provider: SystemSettingsProvider; numbering: DocumentNumberingService; service: SystemSettingsService } {
  const provider = new SystemSettingsProvider(repo);
  const numbering = new DocumentNumberingService(provider, repo);
  numbering.attachCodeResolver({ clientCode: targets.lookups.clientCode, projectCode: targets.lookups.projectCode, unitCode: targets.lookups.unitCode });
  const service = new SystemSettingsService(provider, repo, secrets, probe, audit);
  service.attachUserNameResolver(targets.lookups.userName);

  for (const s of targets.numberedServices) s.attachNumberAllocator(numbering);
  for (const s of targets.endServices) {
    s.attachEndRequirements(async documentType => {
      const end = (await provider.get()).end as Record<string, { requirePhoto: boolean; requireCroqui: boolean } | undefined>;
      const cfg = end[documentType];
      return cfg ? { requirePhoto: cfg.requirePhoto, requireCroqui: cfg.requireCroqui } : null;
    });
  }
  targets.adminService.attachPasswordPolicy(async () => (await provider.get()).security);
  targets.importService.attachMappingOptions(async () => {
    const imp = (await provider.get()).import;
    return { extraAliases: imp.aliases, extraEmptyMarkers: imp.extraEmptyMarkers };
  });
  if (targets.databaseReset) {
    service.attachDatabaseResetHandler(targets.databaseReset.handler, targets.databaseReset.adminAuth);
  }
  // Filtro síncrono: lê o que já está carregado (ou os padrões — que notificam tudo).
  notificationService.attachEventFilter(event => isNotificationEventEnabled(provider.peek().notification, event));
  provider.reload().catch(() => { /* sem registro ainda: valem os padrões */ });

  return { provider, numbering, service };
}
